import React from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Button, Form, FormInputField, FormMessage } from '@/components/common'
import { EntraLoginButton } from '@/features/auth/components/EntraLoginButton/EntraLoginButton'
import { useAuth } from '@/hooks/useAuth'

const loginSchema = z.object({
  email: z.string().email('Email inválido').min(1, 'Email es requerido'),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres')
})

export type LoginFormData = z.infer<typeof loginSchema>

export const LoginForm: React.FC = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const { control, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      await login(data.email, data.password)
      navigate('/dashboard')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido'

      if (
        errorMessage.includes('401') ||
        errorMessage.includes('invalid') ||
        errorMessage.includes('unauthorized')
      ) {
        setError('Email o contraseña incorrectos. Verificá tus credenciales e intentá de nuevo.')
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setError('Error de conexión. Por favor, verifica tu conexión a internet.')
      } else {
        setError('Ha ocurrido un error. Por favor, intenta nuevamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} spacing="lg">
      <EntraLoginButton />

      <div className="relative my-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-3 text-gray-400">o iniciá sesión con tu cuenta</span>
        </div>
      </div>

      {error && <FormMessage type="error">{error}</FormMessage>}

      <FormInputField
        control={control}
        name="email"
        type="email"
        label="Email"
        placeholder="usuario@ejemplo.com"
        autoComplete="email"
        disabled={isLoading}
        isRequired
      />

      <FormInputField
        control={control}
        name="password"
        type="password"
        label="Contraseña"
        placeholder="••••••••"
        autoComplete="current-password"
        disabled={isLoading}
        isRequired
        showPasswordToggle
      />

      <Button type="submit" fullWidth isLoading={isLoading} disabled={isLoading}>
        Iniciar Sesión
      </Button>
    </Form>
  )
}
